import * as THREE from "three";
import { grassDensity } from "../graphicsPrefs";
import { Cell, CellKind, Grid, RoadMap } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import type { Biome } from "../../ui/chapters";
import { detailSurvivesSlope, slopeAmount } from "../groundSlope";
import { lib } from "./common";
import { QUALITY_TIERS, startingTier, tierBand } from "../quality";
import { DETAIL_SETS, DetailFamily, TUFT_HEIGHT, cardMaterial, flatten, frac, hash3, vnoise } from "./detailParts";
import { SCREE_MODELS, SCREE_TINT, screeMeshes } from "./detailScree";

/**
 * Dense ground detail — tufts, flowers, pebbles — scattered over the continuous ground as instanced meshes.
 *
 * The v5 tiles baked their own tufts into every cell, which both pinned the detail to the grid and made it far too
 * big (0.23 units against a 0.33-unit soldier: waist-high grass). Here the tufts are their own tiny models placed by
 * a world-space hash, so the field crosses cell boundaries, and the whole family is normalised to `TuftHeight`.
 */
export class DetailScatter {
  readonly group = new THREE.Group();
  /** How tall a tuft may be, in world units — about a third of a soldier. */
  static readonly TuftHeight = TUFT_HEIGHT;
  /** Decals per cell: 7 put ~0.46 M scatter triangles in a portrait view on level 1; 6 keeps it under 0.4 M. */
  static readonly PerCell = 6;
  /** Side of a scatter chunk in cells: small enough that culling drops the off-screen ground, big enough to keep draws few. */
  static readonly Chunk = 6;   // 12 was ~9,000 instances on the hub; 7 reads as the same meadow
  /** Scatter density multiplier; `?lite` sets 0 (see main.ts). */
  static density = 1;
  /**
   * Density by quality tier (terrain F6). The micro-detail models are still ~1,000 triangles each (the card-clump
   * rebuild is Phase 3), so this is where a phone gets most of its triangles back: 0.8 of the desktop's on `high`.
   */
  static readonly TierDensity: Record<string, number> = { ultra: 1, high: 0.8, medium: 0.5, low: 0.25, lowest: 0.25 };
  /** The tier factor for the device's current tier (the governor saves its step-downs, so this follows them). */
  static tierFactor(): number {
    try { return DetailScatter.TierDensity[tierBand(QUALITY_TIERS[startingTier()] ?? QUALITY_TIERS[0]).name] ?? 1; } catch { return 1; }
  }
  /** Most detail instances one cell may take, in the thickest patch at a rock's foot. */
  static readonly MaxPerCell = 18;
  /**
   * How many of `PerCell` a cell gets, before tier and thinning: clumped by a slow noise into patches (0.15× in the
   * bare stretches to 2× in the thick of a patch, averaging about 1), and at least `EdgeBoost` beside a blocker or
   * a gorge — the contact dressing that makes the reference maps look lived in. A uniform hash, which is what this
   * was, spreads the same count evenly and reads as nothing at all.
   */
  static clusterAt(x: number, z: number): number {
    const n = vnoise(x * 0.3 + 17.1, z * 0.3 + 5.3) * 0.7 + vnoise(x * 0.83 + 2.9, z * 0.83 + 11.7) * 0.3;
    const t = Math.min(1, Math.max(0, (n - 0.3) / 0.42));
    return 0.15 + 1.85 * t * t * (3 - 2 * t);
  }
  static readonly EdgeBoost = 1.6;
  /**
   * How far past the playable rectangle the dense detail keeps going, in cells, and how far out it has thinned to
   * nothing. The ground itself now runs 16 cells past the map; scattering twelve tufts on every one of those ~1700
   * cells is 20 000 instances of a thousand-triangle model, which is a slideshow. Close in it stays dense, then it
   * falls away — and the haze covers the rest.
   */
  static readonly DenseBeyond = 3;
  static readonly ThinBeyond = 9;
  /** The detail families per biome (detailParts.ts DETAIL_SETS). */
  static readonly Sets = DETAIL_SETS;
  /**
   * The card clump (Phase 3, terrain F6): one 6-triangle model (`d_cards`, three crossed quads 0.18 tall) whose
   * instances each pick a tile of its painted 512² atlas — grass, tall grass, flowers, fern, clover, reeds, ash tuft.
   * Every card family of a chunk is ONE instanced draw (the tile rides on an instance attribute), in place of one
   * draw per family per chunk at ~300 triangles a tuft. Families without `cards`, and every family when `d_cards`
   * does not ship, keep their meshes.
   */
  static readonly CardModel = "d_cards";
  /** Atlas layout of `d_cards` (glTF uv, v down): 4 columns x 2 rows. */
  static readonly CardTiles = [4, 2] as const;
  /**
   * The ground's height as drawn (GroundMesh.drawnAt: the triangles on screen, baked), where a
   * tuft stands and what its slope is measured on. The hills alone (`groundAt`) are up to ±0.09 off the drawn sheet,
   * which on a bank left tufts hovering over the downhill side or buried in the uphill one.
   */
  static heightAt(x: number, z: number): number { return GroundMesh.drawnAt(x, z); }
  /** The biome whose detail set is scattered; set alongside `FoliageView.biome` when a level starts. */
  static biome: Biome = "meadow";
  static get Sources(): DetailFamily[] {
    return DetailScatter.Sets[DetailScatter.biome] ?? DetailScatter.Sets.meadow;
  }
  private readonly meshes: THREE.InstancedMesh[] = [];
  /** Per mesh: which grid cell each instance sits in, and the matrices as first built. */
  private readonly cellOf: Int32Array[] = [];
  private readonly baseMatrix: Float32Array[] = [];
  /** Per mesh: each instance's spot (x, z, yaw, scale, hash), and the part's own transform and size. */
  private readonly spotOf: Float32Array[] = [];
  private readonly partOf: { local: THREE.Matrix4; norm: number }[] = [];
  private readonly hidden = new Set<number>();

  constructor(private readonly grid: Grid) {}

  /**
   * Builds the scatter for the level. Call ONCE: it is independent of the road, which only hides the tufts it runs
   * over (see `setRoad`).
   *
   * It used to be rebuilt on every card the player placed, because the controller keys its view refresh off the road
   * tile count. That disposed and recreated every instanced mesh across ~1700 cells — tens of thousands of instances,
   * a `Box3` per family and a fresh `InstancedMesh` per part — for a change that hides a handful of tufts. On a phone
   * it was a hitch on every card, and it is what made the headless level check unusably slow.
   */
  build(): void {
    const L = lib();
    this.clear();
    if (!L) return;
    // one bucket per model: a family's weight is split evenly across whichever of its variants shipped
    const families: string[] = [], weights: number[] = [];
    const cards = L.hq(DetailScatter.CardModel);
    for (const src of DetailScatter.Sources) {
      if (cards && src.cards?.length) {
        // one "family" per atlas tile, named `#<tile>`: they all draw through the card mesh
        for (const t of src.cards) { families.push(`#${t}`); weights.push(src.weight / src.cards.length); }
        continue;
      }
      const have = src.variants.filter((n) => L.hq(n));
      const use = have.length ? have : L.hq(src.fallback) ? [src.fallback] : [];
      for (const n of use) { families.push(n); weights.push(src.weight / use.length); }
    }
    if (families.length === 0) return;
    const total = weights.reduce((a, b) => a + b, 0);
    for (let i = 0; i < weights.length; i++) weights[i] /= total;
    const spots: { family: number; cell: number; x: number; z: number; yaw: number; scale: number; h: number }[] = [];
    const tier = DetailScatter.tierFactor();
    // a free cell beside a blocker or a gorge gets thick dressing, crowded toward that side
    const feature = (x: number, z: number) => {
      const c = new Cell(x, z);
      if (!this.grid.inBounds(c)) return false;
      const k = this.grid.kindAt(c);
      return k === CellKind.Blocked || k === CellKind.Chasm;
    };
    // every cell the ground mesh made land, including the apron beyond the playable rectangle
    const cells: Cell[] = GroundMesh.currentLand
      ? [...GroundMesh.currentLand].map((k) => { const [x, z] = k.split("|").map(Number); return new Cell(x, z); })
      : Array.from({ length: this.grid.cellCount }, (_, i) => this.grid.cellAt(i));
    for (const c of cells) {
      const inside = this.grid.inBounds(c);
      if (inside && this.grid.kindAt(c) !== CellKind.Empty) continue;
      if (inside && this.grid.isBaseCell(c)) continue;
      const out = Math.max(0, -c.x, c.x - (this.grid.width - 1), -c.y, c.y - (this.grid.height - 1));
      if (out > DetailScatter.ThinBeyond) continue;
      const thin = out <= DetailScatter.DenseBeyond ? 1
        : 1 - (out - DetailScatter.DenseBeyond) / (DetailScatter.ThinBeyond - DetailScatter.DenseBeyond);
      const cell = inside ? this.grid.index(c) : -1;
      const sides = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).filter(([dx, dz]) => feature(c.x + dx, c.y + dz));
      let k0 = DetailScatter.clusterAt(c.x, c.y);
      if (sides.length) k0 = Math.max(k0, DetailScatter.EdgeBoost);
      const n = Math.min(DetailScatter.MaxPerCell, Math.round(DetailScatter.PerCell * k0 * thin * DetailScatter.density * grassDensity() * tier));
      for (let k = 0; k < n; k++) {
        const h = hash3(c.x, c.y, k);
        let x = c.x - 0.5 + frac(h * 7.31), z = c.y - 0.5 + frac(h * 3.77);
        if (sides.length && k % 3 !== 2) {
          // two in three crowd into the strip along a feature's side: the foot of the rock, the gorge's lip
          const [dx, dz] = sides[k % sides.length];
          const along = frac(h * 9.17) - 0.5, depth = 0.5 - frac(h * 4.41) * 0.28;
          x = c.x + (dx !== 0 ? dx * depth : along); z = c.y + (dz !== 0 ? dz * depth : along);
        }
        // none on a steep bank or a gorge's shoulder (M30 #16): measured on the ground as drawn
        if (!detailSurvivesSlope(slopeAmount(DetailScatter.heightAt, x, z), frac(h * 11.93))) continue;
        let pick = frac(h * 5.13), family = 0;
        for (let f = 0; f < families.length; f++) { if (pick < weights[f]) { family = f; break; } pick -= weights[f]; }
        spots.push({ family, cell, x, z, yaw: frac(h * 2.17) * Math.PI * 2, scale: 0.6 + frac(h * 13.7) * 0.7, h });
      }
    }
    // One instanced mesh per family part PER CHUNK of ground, so the camera culls what is off screen. A single mesh
    // per family spanned the whole map and its apron and could never be culled: in portrait on a phone ~60% of the
    // scatter (a million triangles) was drawn off the edges of the screen every frame.
    const chunkOf = (s: { x: number; z: number }) => `${Math.floor(s.x / DetailScatter.Chunk)},${Math.floor(s.z / DetailScatter.Chunk)}`;
    // every card family of a chunk together: one instanced mesh, the atlas tile per instance
    const cardFams = new Set(families.map((n, i) => (n.startsWith("#") ? i : -1)).filter((i) => i >= 0));
    if (cardFams.size) {
      const byChunk = new Map<string, typeof spots>();
      for (const s of spots) if (cardFams.has(s.family)) { const k = chunkOf(s); (byChunk.get(k) ?? byChunk.set(k, []).get(k)!).push(s); }
      const [part] = flatten(L.clone(`hq:${DetailScatter.CardModel}`), DetailScatter.CardModel);
      const mat = cardMaterial(part.material);
      const [cols, rows] = DetailScatter.CardTiles;
      for (const picks of byChunk.values()) {
        const geo = part.geometry.clone();
        const tiles = new Float32Array(picks.length * 2);
        picks.forEach((spot, i) => {
          const t = Number(families[spot.family].slice(1));
          tiles[i * 2] = (t % cols) / cols; tiles[i * 2 + 1] = Math.floor(t / cols) / rows;
        });
        geo.setAttribute("aTile", new THREE.InstancedBufferAttribute(tiles, 2));
        this.addInstances(geo, mat, part.local, part.norm, picks, true);
      }
    }
    for (let f = 0; f < families.length; f++) {
      if (cardFams.has(f)) continue;
      const byChunk = new Map<string, typeof spots>();
      for (const s of spots) if (s.family === f) { const k = chunkOf(s); (byChunk.get(k) ?? byChunk.set(k, []).get(k)!).push(s); }
      if (byChunk.size === 0) continue;
      const parts = flatten(L.clone(`hq:${families[f]}`), families[f]);
      for (const picks of byChunk.values()) for (const part of parts) this.addInstances(part.geometry, part.material, part.local, part.norm, picks, false);
    }
    this.hidden.clear();
    this.buildScree();
  }

  /** One instanced mesh of a detail part over a chunk's spots, registered for `setRoad`. */
  private addInstances(geometry: THREE.BufferGeometry, material: THREE.Material, local: THREE.Matrix4, norm: number,
    picks: { cell: number; x: number; z: number; yaw: number; scale: number; h: number }[], ownsGeometry: boolean): void {
    const inst = new THREE.InstancedMesh(geometry, material, picks.length);
    inst.castShadow = false; inst.receiveShadow = true;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3();
    picks.forEach((spot, i) => {
      const k = norm * spot.scale;
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), spot.yaw);
      p.set(spot.x, DetailScatter.heightAt(spot.x, spot.z), spot.z);
      s.set(k, k, k);
      m.compose(p, q, s).multiply(local);
      inst.setMatrixAt(i, m);
    });
    inst.instanceMatrix.needsUpdate = true;
    inst.computeBoundingSphere();
    if (ownsGeometry) this.ownGeometry.add(geometry);
    this.meshes.push(inst);
    this.cellOf.push(Int32Array.from(picks.map((s) => s.cell)));
    this.baseMatrix.push(Float32Array.from(inst.instanceMatrix.array));
    this.spotOf.push(Float32Array.from(picks.flatMap((s) => [s.x, s.z, s.yaw, s.scale, s.h])));
    this.partOf.push({ local, norm });
    this.group.add(inst);
  }
  /** Geometries this view cloned (the card mesh per chunk, for its tile attribute) and must dispose. */
  private readonly ownGeometry = new Set<THREE.BufferGeometry>();

  // ------------------------------------------------------------------------------------------------ gorge scree
  /** The three low-poly scree stones and their tint per biome (detailScree.ts). */
  static readonly ScreeModels = SCREE_MODELS;
  static readonly ScreeTint = SCREE_TINT;
  private readonly scree: THREE.InstancedMesh[] = [];

  /** Half-buried scree along every gorge lip and foot (detailScree.ts); none without a gorge or the stones. */
  private buildScree(): void {
    for (const inst of screeMeshes(DetailScatter.biome)) { this.scree.push(inst); this.group.add(inst); }
  }


  /**
   * Moves the tufts the road now runs over out to its verge, in place.
   *
   * Nothing is rebuilt: each instance keeps its slot and gets a new matrix, a buffer write per affected instance.
   * A tuft on a road cell used to be collapsed to a point; now it is set down in the shoulder beside the road, on a
   * side whose neighbour is not road — so the road's edges carry a line of grass and pebbles, the contact dressing
   * that makes a track look worn into the meadow (terrain F6), for no extra instances. A cell with road on every
   * side hides its tufts. Road cells only ever get added, so the whole road is re-placed each time (a few hundred
   * instances): a verge can become road later, where the track turns.
   */
  setRoad(road: RoadMap | null): void {
    const cells = road?.roadCells ?? [];
    let fresh = false;
    for (const c of cells) {
      const i = this.grid.index(c);
      if (this.hidden.has(i)) continue;
      this.hidden.add(i); fresh = true;
    }
    if (!fresh) return;
    const W = this.grid.width;
    const onRoad = (x: number, z: number) => x >= 0 && z >= 0 && x < W && z < this.grid.height && this.hidden.has(z * W + x);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), p = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
    for (let k = 0; k < this.meshes.length; k++) {
      const inst = this.meshes[k], cellOf = this.cellOf[k], spot = this.spotOf[k], part = this.partOf[k];
      let touched = false;
      const arr = inst.instanceMatrix.array as Float32Array;
      for (let i = 0; i < cellOf.length; i++) {
        const ci = cellOf[i];
        if (ci < 0 || !this.hidden.has(ci)) continue;
        touched = true;
        const cx = ci % W, cz = Math.floor(ci / W);
        const sides = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).filter(([dx, dz]) => !onRoad(cx + dx, cz + dz) && this.grid.inBounds(new Cell(cx + dx, cz + dz)));
        if (sides.length === 0) { arr.fill(0, i * 16, i * 16 + 16); continue; }
        const x = spot[i * 5], z = spot[i * 5 + 1], yaw = spot[i * 5 + 2], s = spot[i * 5 + 3], h = spot[i * 5 + 4];
        const [dx, dz] = sides[Math.floor(frac(h * 6.73) * sides.length) % sides.length];
        // just outside the road stroke's edge (0.43 from the centre line), in the worn shoulder
        const out = DetailScatter.VergeIn + frac(h * 8.29) * (DetailScatter.VergeOut - DetailScatter.VergeIn);
        const along = (dx !== 0 ? z - cz : x - cx) * 0.95;
        const vx = cx + (dx !== 0 ? dx * out : along), vz = cz + (dz !== 0 ? dz * out : along);
        const k2 = part.norm * s * 0.9;
        q.setFromAxisAngle(up, yaw);
        p.set(vx, DetailScatter.heightAt(vx, vz), vz);
        sc.set(k2, k2, k2);
        m.compose(p, q, sc).multiply(part.local);
        m.toArray(arr, i * 16);
      }
      if (touched) inst.instanceMatrix.needsUpdate = true;
    }
  }
  /** How far from a road cell's centre its displaced tufts stand: just past the road's edge, into the shoulder. */
  static readonly VergeIn = 0.47;
  static readonly VergeOut = 0.58;

  clear(): void {
    for (const m of this.meshes) { this.group.remove(m); m.dispose(); }
    for (const g of this.ownGeometry) g.dispose();
    this.ownGeometry.clear();
    for (const m of this.scree) { this.group.remove(m); m.dispose(); }
    this.scree.length = 0;
    this.meshes.length = 0;
    this.cellOf.length = 0;
    this.baseMatrix.length = 0;
    this.spotOf.length = 0;
    this.partOf.length = 0;
    this.hidden.clear();
  }
  dispose(): void { this.clear(); }
}
